import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { competitionsAPI, leaguesAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { TopScorersTab } from '@/components/TopScorersTab';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Trophy, Users, Calendar, Plus, ImagePlus, Settings, Swords, Layers, Clock, Flag
} from 'lucide-react';
import { getInitials, formatDate, getStatusLabel, getStatusColorClass, resolveMediaUrl } from '@/lib/utils';
import { ImageCropUpload } from '@/components/ImageCropUpload';

const FORMAT_LABELS = {
  round_robin: 'Girone all\'italiana',
  groups: 'Gironi',
  elimination: 'Eliminazione diretta',
  groups_elimination: 'Gironi + Tabellone',
  auto: 'Automatico',
};

const ROUND_LABELS_BY_TOTAL = {
  1: ['Finale'],
  2: ['Semifinale', 'Finale'],
  3: ['Quarti', 'Semifinale', 'Finale'],
  4: ['Ottavi', 'Quarti', 'Semifinale', 'Finale'],
  5: ['Sedicesimi', 'Ottavi', 'Quarti', 'Semifinale', 'Finale'],
};

const isEliminationFormat = (format) => format === 'elimination' || format === 'groups_elimination';

const buildRounds = (matches, teamsCount) => {
  if (!matches || matches.length === 0) return [];
  const sorted = [...matches].sort((a, b) => new Date(a.date_time) - new Date(b.date_time));
  const teams = Math.max(2, teamsCount || sorted.length * 2);
  const totalRounds = Math.max(1, Math.ceil(Math.log2(teams)));
  const labels = ROUND_LABELS_BY_TOTAL[totalRounds] || Array.from({ length: totalRounds }, (_, i) => `Round ${i + 1}`);

  const rounds = [];
  let cursor = 0;
  for (let r = 0; r < totalRounds; r += 1) {
    const matchesInRound = Math.max(1, Math.ceil(teams / Math.pow(2, r + 1)));
    const slice = sorted.slice(cursor, cursor + matchesInRound);
    cursor += matchesInRound;
    rounds.push({ label: labels[r], matches: slice });
    if (cursor >= sorted.length) break;
  }
  return rounds;
};

const KpiCard = ({ icon: Icon, label, value, accent }) => (
  <Card className={`overflow-hidden bg-gradient-to-br ${accent}`}>
    <CardContent className="p-3 md:p-4">
      <div className="flex items-center gap-2 text-white/80 text-[11px] uppercase tracking-widest font-medium">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="text-2xl md:text-3xl font-bold text-white mt-1 leading-none">{value}</div>
    </CardContent>
  </Card>
);

const BracketMatchCard = ({ match, index }) => {
  const teamA = match.team_a_name || `Squadra ${index * 2 + 1}`;
  const teamB = match.team_b_name || `Squadra ${index * 2 + 2}`;
  const scoreA = match.result?.team_a_score;
  const scoreB = match.result?.team_b_score;
  const completed = match.status === 'completed';
  const winnerIsA = completed && scoreA > scoreB;
  const winnerIsB = completed && scoreB > scoreA;
  return (
    <Link
      to={`/matches/${match.match_id}`}
      className="block w-56 shrink-0 rounded-xl border bg-card hover:border-primary/50 hover:shadow-md transition-all"
    >
      <div className="px-3 py-2 border-b text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        <Clock className="h-3 w-3" />
        {formatDate(match.date_time)}
      </div>
      <div className="p-3 space-y-2">
        <div className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded-md ${winnerIsA ? 'bg-emerald-500/10 ring-1 ring-emerald-500/40' : 'bg-muted/40'}`}>
          <span className={`text-sm truncate ${winnerIsA ? 'font-semibold' : ''}`}>{teamA}</span>
          <span className="font-bold tabular-nums text-sm">{completed ? scoreA : '—'}</span>
        </div>
        <div className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded-md ${winnerIsB ? 'bg-emerald-500/10 ring-1 ring-emerald-500/40' : 'bg-muted/40'}`}>
          <span className={`text-sm truncate ${winnerIsB ? 'font-semibold' : ''}`}>{teamB}</span>
          <span className="font-bold tabular-nums text-sm">{completed ? scoreB : '—'}</span>
        </div>
      </div>
      <div className="px-3 pb-2">
        <Badge variant="outline" className={`text-[10px] ${getStatusColorClass(match.status)}`}>
          {getStatusLabel(match.status)}
        </Badge>
      </div>
    </Link>
  );
};

const BracketView = ({ rounds }) => (
  <div className="overflow-x-auto -mx-4 md:mx-0 no-scrollbar">
    <div className="flex items-stretch gap-6 md:gap-8 px-4 md:px-0 py-2 min-w-max">
      {rounds.map((round, i) => (
        <div key={i} className="flex flex-col gap-3 md:gap-4 justify-around">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold sticky top-0">
            {round.label}
          </p>
          <div className="flex flex-col gap-3 md:gap-6 justify-around flex-1">
            {round.matches.map((m, idx) => (
              <BracketMatchCard key={m.match_id} match={m} index={idx} />
            ))}
            {round.matches.length === 0 && (
              <div className="w-56 h-24 rounded-xl border-2 border-dashed border-muted flex items-center justify-center text-xs text-muted-foreground">
                In attesa
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const CompetitionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [competition, setCompetition] = useState(null);
  const [league, setLeague] = useState(null);
  const [sport, setSport] = useState(null);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [leagueMembers, setLeagueMembers] = useState([]);
  const [membership, setMembership] = useState(null);
  const [showCoverEdit, setShowCoverEdit] = useState(false);

  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: '', members: [] });
  const [savingTeam, setSavingTeam] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await competitionsAPI.getById(id);
      setCompetition(res.data.competition);
      setLeague(res.data.league);
      setSport(res.data.sport);
      setTeams(res.data.teams || []);
      setMatches(res.data.matches || []);

      if (res.data?.competition?.league_id) {
        const leagueRes = await leaguesAPI.getById(res.data.competition.league_id);
        setLeagueMembers(leagueRes.data.members || []);
        setMembership(leagueRes.data.user_membership || null);
      }
    } catch (error) {
      console.error('Error fetching competition:', error);
      toast.error('Errore nel caricamento competizione');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const isAdmin = membership?.role === 'admin' || membership?.role === 'manager' || user?.role === 'admin';
  const isTournament = competition?.competition_type === 'tournament';
  const effectiveFormat = competition?.effective_format || competition?.format;
  const showBracket = isTournament && isEliminationFormat(effectiveFormat) && matches.length > 0;
  const rounds = useMemo(
    () => (showBracket ? buildRounds(matches, teams.length) : []),
    [showBracket, matches, teams.length]
  );
  const selectedTeamMembers = useMemo(
    () => leagueMembers.filter(member => teamForm.members.includes(member.user_id)),
    [leagueMembers, teamForm.members]
  );
  const selectedTeamSkillSum = selectedTeamMembers.reduce(
    (sum, member) => sum + (member.skill_level ?? 3),
    0
  );

  const heroAccent = isTournament
    ? 'from-amber-500 via-orange-500 to-rose-500'
    : 'from-blue-500 via-indigo-500 to-violet-500';

  const handleCoverChange = async (url) => {
    try {
      await competitionsAPI.update(id, { cover_image_url: url });
      setCompetition(prev => ({ ...prev, cover_image_url: url }));
      setShowCoverEdit(false);
      if (url) toast.success('Copertina aggiornata');
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    }
  };

  const toggleMember = (memberId) => {
    setTeamForm(prev => {
      const exists = prev.members.includes(memberId);
      return {
        ...prev,
        members: exists ? prev.members.filter(id => id !== memberId) : [...prev.members, memberId]
      };
    });
  };

  const handleCreateTeam = async () => {
    if (!teamForm.name) {
      toast.error('Nome squadra obbligatorio');
      return;
    }
    setSavingTeam(true);
    try {
      await competitionsAPI.addTeam(id, { name: teamForm.name, members: teamForm.members });
      toast.success('Squadra creata');
      setShowTeamDialog(false);
      setTeamForm({ name: '', members: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione squadra');
    } finally {
      setSavingTeam(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-56 w-full" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20" />)}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!competition) {
    return (
      <div className="p-4 md:p-6 lg:p-8 text-center">
        <Trophy className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Competizione non trovata</h2>
        <Link to="/leagues">
          <Button>Torna alle leghe</Button>
        </Link>
      </div>
    );
  }

  const playedMatches = matches.filter(m => m.status === 'completed').length;

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6 pb-24" data-testid="competition-detail">
      {/* Hero */}
      <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${heroAccent}`}>
        {competition.cover_image_url && (
          <img
            src={resolveMediaUrl(competition.cover_image_url)}
            alt={competition.name}
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        {/* decorative blobs */}
        <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 w-72 h-72 rounded-full bg-white/10 blur-3xl" />

        <div className="relative p-5 md:p-8">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="px-2.5 py-1 rounded-full bg-white/20 backdrop-blur text-white text-[11px] uppercase tracking-widest font-semibold flex items-center gap-1.5">
              {isTournament ? <Trophy className="h-3 w-3" /> : <Layers className="h-3 w-3" />}
              {isTournament ? 'Torneo' : 'Campionato'}
            </span>
            {sport?.name && (
              <span className="px-2.5 py-1 rounded-full bg-white/20 backdrop-blur text-white text-[11px] uppercase tracking-widest">
                {sport.name}
              </span>
            )}
            <Badge className={`${getStatusColorClass(competition.status)} border-0`}>
              {getStatusLabel(competition.status)}
            </Badge>
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white drop-shadow-lg">
            {competition.name}
          </h1>
          {league && (
            <Link to={`/leagues/${league.league_id}`} className="inline-flex items-center gap-1 mt-1 text-white/85 hover:text-white text-sm">
              <Flag className="h-3.5 w-3.5" />
              {league.name}
            </Link>
          )}

          <div className="flex flex-wrap gap-2 mt-5">
            {isAdmin && (
              <Button
                variant="secondary"
                size="sm"
                className="bg-white/15 hover:bg-white/25 backdrop-blur text-white border-0"
                onClick={() => setShowCoverEdit(true)}
              >
                <ImagePlus className="h-4 w-4 mr-2" />
                Copertina
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="secondary"
                size="sm"
                className="bg-white/15 hover:bg-white/25 backdrop-blur text-white border-0"
                onClick={() => navigate(`/competitions/${competition.competition_id}/edit`)}
              >
                <Settings className="h-4 w-4 mr-2" />
                Modifica
              </Button>
            )}
            {isAdmin && (
              <Button
                size="sm"
                className="bg-white text-foreground hover:bg-white/90"
                onClick={() => navigate(`/matches/new?league=${competition.league_id}&competition=${competition.competition_id}`)}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Nuova Partita
              </Button>
            )}
            {isAdmin && competition.registration_mode === 'teams' && (
              <Button
                size="sm"
                className="bg-white text-foreground hover:bg-white/90"
                onClick={() => setShowTeamDialog(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nuova Squadra
              </Button>
            )}
          </div>
        </div>

        {showCoverEdit && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/90 p-4">
            <div className="w-full max-w-md">
              <ImageCropUpload
                value={competition.cover_image_url}
                onChange={handleCoverChange}
                preset="cover"
              />
              <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={() => setShowCoverEdit(false)}>
                Annulla
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 -mt-4 relative z-10 px-1">
        <KpiCard
          icon={Users}
          label={competition.registration_mode === 'teams' ? 'Squadre' : 'Iscritti'}
          value={teams.length || '—'}
          accent="from-blue-500 to-blue-600"
        />
        <KpiCard
          icon={Calendar}
          label="Partite"
          value={matches.length || '—'}
          accent="from-emerald-500 to-emerald-600"
        />
        <KpiCard
          icon={Trophy}
          label="Giocate"
          value={playedMatches}
          accent="from-amber-500 to-amber-600"
        />
        <KpiCard
          icon={Swords}
          label="Formato"
          value={FORMAT_LABELS[effectiveFormat] || effectiveFormat || '—'}
          accent="from-violet-500 to-violet-600"
        />
      </div>

      {/* Description / Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Dettagli</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className={competition.description ? 'text-foreground/80' : 'text-muted-foreground italic'}>
              {competition.description || 'Nessuna descrizione'}
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 pt-2 text-muted-foreground">
              {competition.season_label && (
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  Stagione: <strong className="text-foreground">{competition.season_label}</strong>
                </span>
              )}
              {competition.start_date && (
                <span>Inizio: <strong className="text-foreground">{formatDate(competition.start_date)}</strong></span>
              )}
              {competition.end_date && (
                <span>Fine: <strong className="text-foreground">{formatDate(competition.end_date)}</strong></span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Tipo</span>
              <Badge variant="secondary">{isTournament ? 'Torneo' : 'Campionato'}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Iscrizione</span>
              <Badge variant="outline">{competition.registration_mode === 'teams' ? 'A squadre' : 'A giocatori'}</Badge>
            </div>
            {competition.max_teams && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Max squadre</span>
                <span className="font-medium">{competition.max_teams}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Teams */}
      {competition.registration_mode === 'teams' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Squadre <span className="text-muted-foreground text-sm font-normal">({teams.length})</span>
            </CardTitle>
            {isAdmin && (
              <Button size="sm" variant="ghost" onClick={() => setShowTeamDialog(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Aggiungi
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {teams.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-10 w-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Nessuna squadra registrata</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {teams.map((team, idx) => {
                  const palettes = [
                    'from-blue-500/10 to-blue-500/5 border-blue-500/30',
                    'from-emerald-500/10 to-emerald-500/5 border-emerald-500/30',
                    'from-amber-500/10 to-amber-500/5 border-amber-500/30',
                    'from-rose-500/10 to-rose-500/5 border-rose-500/30',
                    'from-violet-500/10 to-violet-500/5 border-violet-500/30',
                  ];
                  const accent = palettes[idx % palettes.length];
                  return (
                    <div key={team.team_id} className={`rounded-xl border bg-gradient-to-br ${accent} p-4`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold">{team.name}</h4>
                        <div className="flex items-center gap-1.5">
                          <Badge variant="secondary" className="text-xs">
                            {team.members_info?.length || 0}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {team.skill_level_sum ?? 0} pt skill
                          </Badge>
                        </div>
                      </div>
                      {team.members_info?.length ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="flex -space-x-2">
                            {team.members_info.slice(0, 5).map(m => (
                              <Avatar key={m.user_id} className="h-7 w-7 ring-2 ring-background">
                                <AvatarImage src={m.picture} />
                                <AvatarFallback className="text-[10px]">{getInitials(m.name)}</AvatarFallback>
                              </Avatar>
                            ))}
                          </div>
                          {team.members_info.length > 5 && (
                            <span className="text-xs text-muted-foreground">+{team.members_info.length - 5}</span>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">Nessun membro</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Matches / Bracket */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {showBracket ? <Trophy className="h-5 w-5 text-amber-500" /> : <Calendar className="h-5 w-5 text-primary" />}
            {showBracket ? 'Tabellone' : 'Partite'}
            <span className="text-muted-foreground text-sm font-normal">({matches.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {matches.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <Calendar className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Nessuna partita programmata</p>
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3"
                  onClick={() => navigate(`/matches/new?league=${competition.league_id}&competition=${competition.competition_id}`)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Crea la prima
                </Button>
              )}
            </div>
          ) : showBracket ? (
            <BracketView rounds={rounds} />
          ) : (
            <div className="space-y-2">
              {matches.map(match => {
                const completed = match.status === 'completed';
                return (
                  <Link
                    key={match.match_id}
                    to={`/matches/${match.match_id}`}
                    className="flex items-center justify-between p-3 rounded-lg border hover:border-primary/50 hover:shadow-sm transition-all group"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate group-hover:text-primary transition-colors">{match.title}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <Clock className="h-3 w-3" />
                        {formatDate(match.date_time)}
                      </p>
                    </div>
                    {completed && match.result && (
                      <span className="text-lg font-bold tabular-nums px-3">
                        {match.result.team_a_score}–{match.result.team_b_score}
                      </span>
                    )}
                    <Badge className={getStatusColorClass(match.status)}>
                      {getStatusLabel(match.status)}
                    </Badge>
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cannonieri della competizione */}
      <TopScorersTab scope="competition" scopeId={competition.competition_id} />

      {/* Team Dialog */}
      <Dialog open={showTeamDialog} onOpenChange={setShowTeamDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuova Squadra</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome squadra</Label>
              <Input
                value={teamForm.name}
                onChange={(e) => setTeamForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Membri (opzionale)</Label>
              <div className="max-h-56 overflow-auto border rounded-lg p-2 space-y-2">
                {leagueMembers.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nessun membro</p>
                ) : (
                  leagueMembers.map(m => (
                    <label key={m.user_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-2 py-1">
                      <input
                        type="checkbox"
                        checked={teamForm.members.includes(m.user_id)}
                        onChange={() => toggleMember(m.user_id)}
                      />
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={m.user?.picture} />
                        <AvatarFallback className="text-[10px]">{getInitials(m.user?.name)}</AvatarFallback>
                      </Avatar>
                      <span>{m.user?.name || m.user_id}</span>
                      <Badge variant="outline" className="ml-auto text-[10px] px-1.5 py-0">
                        L{m.skill_level ?? 3}
                      </Badge>
                    </label>
                  ))
                )}
              </div>
              {selectedTeamMembers.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Livello squadra: <strong className="text-foreground">{selectedTeamSkillSum} pt skill</strong>
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTeamDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreateTeam} disabled={savingTeam}>
              {savingTeam ? 'Salvataggio...' : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompetitionDetailPage;
